import React from 'react'
import PropTypes from 'prop-types'
import TeamStanding from './TeamStanding'

const THIRD_QUALIFIED_COUNT = 8

const breakTiesThirds = (arr) => {
	return [...arr].sort((a, b) => {
		if (a.points !== b.points) return b.points - a.points
		if (a.goalsDifference !== b.goalsDifference)
			return b.goalsDifference - a.goalsDifference
		if (a.goalsFor !== b.goalsFor) return b.goalsFor - a.goalsFor
		return a.name.localeCompare(b.name)
	})
}

const ThirdPlaceTable = ({ standingsByGroup, urlAssets }) => {
	const [isInfoOpen, setIsInfoOpen] = React.useState(false)
	const popoverRef = React.useRef(null)
	const buttonRef = React.useRef(null)

	React.useEffect(() => {
		if (!isInfoOpen) return

		const onKeyDown = (e) => {
			if (e.key === 'Escape') setIsInfoOpen(false)
		}

		const onClickOutside = (e) => {
			const pop = popoverRef.current
			const btn = buttonRef.current
			if (!pop || !btn) return

			if (pop.contains(e.target) || btn.contains(e.target)) return
			setIsInfoOpen(false)
		}

		window.addEventListener('keydown', onKeyDown)
		window.addEventListener('mousedown', onClickOutside)

		return () => {
			window.removeEventListener('keydown', onKeyDown)
			window.removeEventListener('mousedown', onClickOutside)
		}
	}, [isInfoOpen])

	// 1) saco el 3ro de cada grupo (index 2) + guardo su grupo
	const thirdsRaw = Object.keys(standingsByGroup || {})
		.sort()
		.map((groupLetter) => {
			const third = standingsByGroup?.[groupLetter]?.[2]
			if (!third) return null

			return {
				...third,
				__group: groupLetter,
			}
		})
		.filter(Boolean)

	// 2) ordeno con tus criterios
	const thirdsSorted = breakTiesThirds(thirdsRaw)

	// 3) marco top 8
	const thirdsWithMeta = thirdsSorted.map((t, idx) => ({
		...t,
		_position: idx + 1,
		_qualified: idx < THIRD_QUALIFIED_COUNT,
	}))

	const buttonLabel = 'Mostrar los resultados'
	const popoverText =
		'Las llaves se activarán cuando la FIFA publique los criterios de emparejamiento.'

	return (
		<section className="my-4 third-place">
			<h2 className="text-center group-name third-place__title">
				TABLA DE LOS TERCEROS DE CADA GRUPO
			</h2>

			<p className="text-center mb-3 third-place__subtitle">
				Los ocho mejores clasifican a la siguiente fase
			</p>

			<section className="third-place__table-wrap">
				<table className="table table-borderless mb-0 text-center third-place__table">
					<thead className="standing-head color-black">
						<tr>
							<th scope="col" className="text-start ps-2">
								Equipo
							</th>
							<th scope="col">PJ</th>
							<th scope="col">PG</th>
							<th scope="col">PE</th>
							<th scope="col">PP</th>

							<th scope="col" className="third-place__hide">
								GF
							</th>
							<th scope="col" className="third-place__hide">
								GC
							</th>

							<th scope="col">Dif.</th>
							<th scope="col">Pts.</th>
						</tr>
					</thead>

					<tbody>
						{thirdsWithMeta.map((team) => {
							const statusClass = team._qualified
								? 'third-bg__table'
								: 'eliminated-team'

							const posClass = `third-place__row third-place__row--pos-${team._position}`
							const nameWithGroup = `${team.name} (${team.__group})`

							return (
								<TeamStanding
									key={`${team.id}-${team.__group}`}
									positionClass={`${statusClass} ${posClass}`}
									slug={team.slug}
									name={nameWithGroup}
									playedMatches={team.playedMatches}
									wonMatches={team.wonMatches}
									drawnMatches={team.drawnMatches}
									lostMatches={team.lostMatches}
									goalsFor={team.goalsFor}
									goalsAgainst={team.goalsAgainst}
									goalsDifference={team.goalsDifference}
									points={team.points}
									urlAssets={urlAssets}
								/>
							)
						})}
					</tbody>
				</table>
			</section>

			{/* CTA al final + popup */}
			<div className="third-place__cta">
				<div className="third-place__cta-popover">
					<button
						ref={buttonRef}
						type="button"
						className="third-place__cta-btn"
						aria-haspopup="dialog"
						aria-expanded={isInfoOpen}
						onClick={() => setIsInfoOpen((v) => !v)}>
						{buttonLabel}
					</button>

					{isInfoOpen && (
						<div
							ref={popoverRef}
							role="dialog"
							aria-label="Información"
							className="third-place__popover">
							<p className="third-place__popover-text">
								{popoverText}
							</p>
							<button
								type="button"
								className="third-place__popover-close"
								onClick={() => setIsInfoOpen(false)}>
								Entendido
							</button>
						</div>
					)}
				</div>
			</div>
		</section>
	)
}

export default ThirdPlaceTable

ThirdPlaceTable.propTypes = {
	standingsByGroup: PropTypes.object.isRequired,
	urlAssets: PropTypes.string.isRequired,
}
